// react
import { useState, useCallback, useMemo, useEffect, FC } from 'react'
// viem
import { createPublicClient, http } from 'viem'
import { arbitrum } from 'viem/chains'
// components
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Tooltip } from '@/components/Tooltip'
// i18n
import { useTranslation } from '@/hooks'
// types
import { EVMDepositViewProps } from './types'
// utils
import BigNumber from 'bignumber.js'
import {
  formatBalanceDisplay,
  shouldShowLargeDepositWarning,
  getEffectiveMaxAmountPure,
} from './utils'
import {
  isGreaterThan,
  formatAmount,
  validateAndFormatInput,
} from '@/components/Deposit/src/utils'
import { setDepositLoading } from '@/components/Deposit/src/DepositManager'
// hooks
import {
  useDepositButtonState,
  useApprove,
} from '@/components/Deposit/src/components/evm/hooks'
import { useMulticallBalance } from '@/components/Deposit/src/hooks/useMulticallBalance'
import { useErc20BalanceOfWatcher } from '@/components/Deposit/src/hooks/useErc20BalanceOfWatcher'
// assets
import MPCDepositWarnIcon from '@/icons/mpc-deposit-warn-icon.png'

const EVM: FC<EVMDepositViewProps> = ({
  address,
  currentActiveChain,
  selectedToken,
  accountType,
  platformBalance,
  isLoading,
  wagmiHooks,
  onChainChange,
  onDeposit,
}) => {
  // i18n
  const { t } = useTranslation()
  const account = wagmiHooks.useAccount()
  const tokenInfo = currentActiveChain.tokenList.find(
    t => t.token === selectedToken || t.symbol === selectedToken
  )
  const selectedChainId = currentActiveChain.chainId

  const [depositAmount, setDepositAmount] = useState('')

  // 监听币种和链切换，清空输入金额
  useEffect(() => {
    setDepositAmount('')
  }, [selectedToken, currentActiveChain.chainId])

  const { multicallResult, queryComponents } = useMulticallBalance({
    account: address,
    chainId: currentActiveChain.chainId,
    contractAddress:
      tokenInfo?.contractAddress || currentActiveChain?.contractAddress,
    tokens: currentActiveChain?.tokenList || [],
    wagmiHooks,
  })

  // 获取当前选中代币的余额
  const currentTokenBalance = useMemo(() => {
    if (!tokenInfo) return '0'

    // 如果是基础代币（ETH等）
    if (
      !tokenInfo.tokenAddress ||
      tokenInfo.tokenAddress.toLowerCase() ===
        '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
    ) {
      return multicallResult.baseTokenBalance.formatted
    }

    // 如果是 ERC20 代币
    const tokenData = multicallResult.combinedData[tokenInfo.tokenAddress]
    return tokenData?.balance?.formatted || '0'
  }, [tokenInfo, multicallResult])

  const ARB_USDT_ADDRESS = '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9'
  const POOL_ADDRESS = '0xceeed84620e5eb9ab1d6dfc316867d2cda332e41'

  // 使用 viem 读取 Arbitrum 主网 USDT 资金池余额
  const publicClient = useMemo(
    () =>
      createPublicClient({
        chain: arbitrum,
        transport: http(
          currentActiveChain.rpcUrl ||
            window?.ENV?.['CHAIN_RPC_URL_42161'] ||
            window?.ENV?.['CHAIN_RPC_URL_421614']
        ),
      }),
    [currentActiveChain.rpcUrl]
  )
  // 查询失败或尚未完成时的安全默认值（超大值，避免误触发 LayerZero 提示）
  const FALLBACK_MAX_POOL_BALANCE = '1000000000000' // 1e12 USDT
  const { formatted: poolUSDTBalanceFormatted } = useErc20BalanceOfWatcher({
    client: publicClient,
    tokenAddress: ARB_USDT_ADDRESS,
    holder: POOL_ADDRESS,
    decimals: 6,
    fallbackFormatted: FALLBACK_MAX_POOL_BALANCE,
    pollingInterval: 12_000,
  })

  /************************ Approve ************************/
  const [isApproving, setIsApproving] = useState(false)
  // Encapsulated approve hook: returns approved state and onApprove action
  const { approve, onApprove } = useApprove({
    account: address,
    accountChainId: account.chainId,
    targetChainId: +currentActiveChain.chainId,
    tokenInfo,
    amount: depositAmount,
    wagmiHooks,
  })

  // Bind UI button to hook's onApprove
  const handleApprove = useCallback(async () => {
    if (isApproving) return
    try {
      setIsApproving(true)
      await onApprove()
    } catch (error) {
      console.error('Approve failed:', error)
    } finally {
      setIsApproving(false)
    }
  }, [isApproving, onApprove])

  /************************ Network Switch ************************/
  // 检查网络是否匹配
  const isNetworkMismatch = useMemo(() => {
    return +account.chainId !== +currentActiveChain.chainId
  }, [account.chainId, currentActiveChain.chainId])

  const handleSwitchNetwork = useCallback(async () => {
    if (onChainChange) {
      onChainChange(selectedChainId)
    }
  }, [onChainChange, selectedChainId])

  useEffect(() => {
    if (isNetworkMismatch) {
      handleSwitchNetwork()
    }
  }, [isNetworkMismatch, handleSwitchNetwork])

  /************************ LayerZero ************************/
  const { showLayerZeroTip, layerZeroTipKey } = useMemo(() => {
    const isArbitrum =
      +currentActiveChain.chainId === 42161 ||
      +currentActiveChain.chainId === 421614
    const isUSDT = selectedToken?.toUpperCase() === 'USDT'
    const amountBN = new BigNumber(depositAmount || '0')
    const thresholdBN = new BigNumber('200000')

    // 默认不展示
    let show = false
    let key: string | undefined = undefined

    if (isArbitrum && isUSDT && depositAmount) {
      // 规则 1：金额 >= 200000 优先
      if (amountBN.gte(thresholdBN)) {
        show = true
        key = 'deposit.layerZeroDepositTipGte200k'
      } else {
        // 规则 2：金额 < 200000 且 >= 资金池余额
        const poolFormatted = poolUSDTBalanceFormatted

        const hasPool =
          typeof poolFormatted === 'string' && poolFormatted.length > 0
        if (hasPool) {
          const poolBN = new BigNumber(poolFormatted as string)
          if (!poolBN.isNaN() && amountBN.gte(poolBN)) {
            show = true
            key = 'deposit.layerZeroDepositTipExceedPool'
          }
        }
      }
    }

    return { showLayerZeroTip: show, layerZeroTipKey: key }
  }, [
    currentActiveChain.chainId,
    selectedToken,
    depositAmount,
    poolUSDTBalanceFormatted,
  ])

  const getEffectiveMaxAmount = useCallback(() => {
    return getEffectiveMaxAmountPure(
      +currentActiveChain.chainId,
      currentTokenBalance || '0',
      selectedToken
    )
  }, [currentTokenBalance, currentActiveChain?.chainId, selectedToken])

  const showLargeDepositWarning = useMemo(() => {
    return shouldShowLargeDepositWarning(
      +currentActiveChain.chainId,
      depositAmount,
      selectedToken
    )
  }, [currentActiveChain?.chainId, depositAmount, selectedToken])

  /************************ Deposit Button State ************************/
  const depositButtonState = useDepositButtonState({
    isNetworkMismatch,
    depositAmount,
    approved: approve,
    currentTokenBalance,
    selectedToken,
    showLayerZeroTip,
    minDeposit: 10,
  })

  // 处理输入变化的函数
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const maxBalance = getEffectiveMaxAmount()

    // 验证和格式化输入
    const validation = validateAndFormatInput(inputValue, maxBalance)

    if (validation.isValid) {
      // 如果输入有效，更新值
      setDepositAmount(validation.formattedValue)

      // 如果是自动调整到最大值，可以在这里添加用户提示（可选）
      if (validation.errorType === 'auto_adjusted_to_max') {
        // 可以在这里添加一个轻微的视觉反馈，比如短暂高亮
        // 或者显示一个提示消息，但为了简洁，我们暂时不添加
      }
    } else {
      // 如果输入无效，根据错误类型处理
      if (validation.errorType === 'exceed_balance') {
        // 这个分支现在应该不会被触发，因为我们改为自动调整
        return
      } else {
        // 其他错误（无效字符等），不更新输入值
        return
      }
    }
  }

  // 处理充值
  const handleDeposit = useCallback(async () => {
    // 设置 loading 状态
    setDepositLoading(true)

    try {
      // 传递必要的充值信息
      if (onDeposit) {
        onDeposit({
          amount: depositAmount,
          chainId: selectedChainId,
          tokenAddress: tokenInfo?.tokenAddress || '',
          tokenSymbol: selectedToken,
          // 透传父组件的账户类型，便于上层业务区分
          accountType,
        })
      }
    } catch (error) {
      console.error('Deposit error:', error)
      setDepositLoading(false)
    }
    // 成功的情况下，loading 状态会在 closeDeposit() 被调用时自动清除
  }, [
    onDeposit,
    depositAmount,
    selectedChainId,
    tokenInfo?.tokenAddress,
    accountType,
    selectedToken,
  ])

  return (
    <>
      {/* 渲染代币查询组件（逻辑组件，不显示UI） */}
      {queryComponents}

      {/* Amount Input Section */}
      <div className="bg-fill-input-primary rounded-lg px-3 py-3 my-4">
        {/* Amount Input 和 MAX Button 在同一行 - 高度 18px */}
        <div className="flex justify-between items-center h-[18px] mb-3">
          <input
            type="text"
            placeholder={t('deposit.amount')}
            value={depositAmount}
            onChange={handleInputChange}
            className="flex-1 bg-transparent text-text-primary text-sm font-medium placeholder-text-tertiary placeholder:font-normal focus:outline-none border-none p-0 h-full"
          />
          <button
            onClick={() => {
              const maxAmount = getEffectiveMaxAmount()
              setDepositAmount(maxAmount)
            }}
            className="text-theme-normal text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity h-full flex items-center"
          >
            {t('deposit.max')}
          </button>
        </div>

        {/* Divider Line */}
        <div className="h-[1px] bg-line-divider-secondary mb-2"></div>

        {/* Available Balance */}
        <div className="flex justify-between items-center">
          <Tooltip content={t('deposit.availableDesc')}>
            <span className="text-text-tertiary text-xs leading-[14px] border-b border-dashed border-line-divider-primary">
              {t('deposit.available')}
            </span>
          </Tooltip>
          <span className="text-text-primary text-xs leading-[14px]">
            {formatBalanceDisplay(
              currentTokenBalance || '0',
              depositAmount,
              selectedToken
            )}
          </span>
        </div>
      </div>

      {/* Token Approval Section */}
      {!approve && isGreaterThan(depositAmount || '0', 0) && (
        <div className="flex justify-center flex-col items-center">
          <h3 className="text-text-primary text-sm leading-4 font-medium">
            {t('deposit.enableSystemProWithToken', {
              token: selectedToken,
              network: currentActiveChain?.chain,
            })}
          </h3>
          <p className="text-text-secondary text-xs leading-3 mt-4">
            {t('deposit.enableTokenDesc', {
              token: selectedToken,
              network: currentActiveChain?.chain,
            })}
          </p>
          <button
            className="w-[180px] h-8 bg-fill-btn-primary hover:bg-fill-btn-primary-hover text-text-on-sec-btn rounded-xl text-sm font-medium transition-colors my-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            onClick={handleApprove}
            disabled={isApproving}
          >
            {isApproving ? (
              <LoadingSpinner size="xs" inline />
            ) : (
              t('deposit.enableWithToken', {
                token: selectedToken,
              })
            )}
          </button>
        </div>
      )}

      {/* LayerZero 提示 - 由父组件控制 */}
      {showLayerZeroTip && (
        <>
          <div className="flex mb-4">
            <div className="w-5">
              <img src={MPCDepositWarnIcon} className="w-4 h-4" alt="Warning" />
            </div>
            <div className="flex-1 text-xs text-functional-orange-1">
              {t(layerZeroTipKey || 'deposit.layerZeroDepositTip')}
            </div>
          </div>
          <div className="my-2 ml-5 h-[1px] border-b border-dashed border-line-divider-secondary"></div>
        </>
      )}

      {/* Warning Messages */}
      {showLargeDepositWarning && (
        <div className="flex mb-4">
          <div className="w-5">
            <img src={MPCDepositWarnIcon} className="w-4 h-4" alt="Warning" />
          </div>
          <div className="flex-1 text-xs text-functional-orange-1">
            {t('deposit.depositTipFor25w')}
          </div>
        </div>
      )}

      {showLargeDepositWarning && (
        <div className="my-2 ml-5 h-[1px] border-b border-dashed border-line-divider-secondary"></div>
      )}

      <div className="flex mb-4">
        <div className="w-5">
          {!(showLargeDepositWarning || showLayerZeroTip) && (
            <img src={MPCDepositWarnIcon} className="w-4 h-4" alt="Warning" />
          )}
        </div>
        <div className="flex-1 text-xs text-functional-orange-1">
          {t('deposit.noPrivateKeyWalletWarning')}
        </div>
      </div>

      {/* Equity Display */}
      <div className="border-t border-line-divider-secondary border-dashed py-4">
        <div className="flex justify-between items-center h-[18px] leading-4">
          <span className="text-sm text-text-tertiary">
            {t('deposit.arrivalTime')}
          </span>

          <Tooltip
            content={
              <div className="grid flex-grow max-w-[300px] min-w-[80px] rounded-2xl flex-col gap-[10px]">
                <p>
                  {t('deposit.arrivaltimeTooltip1', {
                    v:
                      (currentActiveChain?.blockTime *
                        currentActiveChain.txConfirm) /
                      60,
                    v1: currentActiveChain?.txConfirm,
                  })}
                </p>
                <p>{t('deposit.arrivaltimeTooltip2')}</p>
              </div>
            }
            placement="top"
          >
            <span className="text-text-primary font-medium text-right border-b-[2px] border-dashed border-line-divider-primary decoration-dashed text-sm">
              {t('deposit.arrivalEstimated', {
                v:
                  (currentActiveChain?.blockTime *
                    currentActiveChain.txConfirm) /
                  60,
              })}
            </span>
          </Tooltip>
        </div>

        <div className="flex justify-between items-center h-[18px] leading-4 mt-2">
          <span className="text-sm text-text-tertiary">
            {t('deposit.equity')}
          </span>
          <span className="text-sm text-text-primary font-medium">
            {depositAmount ? (
              <span className="flex items-center gap-1">
                {formatAmount(platformBalance)}
                <span className="text-sm font-edgex-harmony text-functional-green-1 leading-[18px] font-medium">
                  →
                </span>
                {formatAmount(
                  (
                    parseFloat(platformBalance) + parseFloat(depositAmount)
                  ).toString()
                )}{' '}
                {selectedToken}
              </span>
            ) : (
              `${formatAmount(platformBalance)} ${selectedToken}`
            )}
          </span>
        </div>
      </div>

      {/* 充值按钮 */}
      <button
        className={`w-full h-[38px] text-sm rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
          depositButtonState.disabled || isLoading
            ? 'bg-text-disabled text-text-secondary cursor-not-allowed'
            : 'bg-fill-btn-primary text-text-on-sec-btn'
        }`}
        onClick={handleDeposit}
        disabled={depositButtonState.disabled || isLoading}
      >
        {(isLoading || isNetworkMismatch) && <LoadingSpinner size="xs" />}
        {depositButtonState.text}
      </button>
    </>
  )
}

export default EVM
